import {
  DestroyRef,
  EventEmitter,
  Injectable,
  Injector,
  Signal,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { ScenePath } from '@models';
import { AnatomoService, ObjectoService } from '@scenes';
import { MlTrackingService } from '@services';
import { SceneStore } from '@stores';
import { v4 as uuidv4 } from 'uuid';

const DETECTION_INTERVALL = 400;

@Injectable({
  providedIn: 'root',
})
export class SimulationService {
  readonly destroy: DestroyRef = inject(DestroyRef);

  private injector = inject(Injector);
  private mlTrackingService = inject(MlTrackingService);
  private sceneStore = inject(SceneStore);

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  sceneDetectionService: any;
  finalRecordDuration: string;
  detectionTimeout: NodeJS.Timeout;
  mediaRecorder: MediaRecorder;
  chunks: Blob[] = [];
  uploadBlob: Blob;
  timerInterval: NodeJS.Timeout;
  delayedTimer: NodeJS.Timeout;

  state = signal<'camera' | 'simulation'>('camera');
  isSimulationRunning = signal(false);
  isExerciseStarted = signal(false);
  isExerciseStopped = signal(false);
  recordingTime = signal(null);
  recordingStartTime: string = null;
  recordingEndTime: string = null;

  nextCycle = new EventEmitter<void>();
  endSimulation = new EventEmitter<void>();

  constructor() {
    effect(() => {
      const scene = this.sceneStore.path();
      if (scene) {
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        this.sceneDetectionService = this.injector.get<any>(
          scene === ScenePath.Objecto
            ? ObjectoService
            : scene === ScenePath.Anatomo
              ? AnatomoService
              : undefined,
        );
      }
    });
  }

  startDetection() {
    this.mlTrackingService.initalizeTracking();
    setTimeout(() => {
      const runDetection = async () => {
        this.nextCycle.emit();
        this.sceneDetectionService.runDetection();
        requestAnimationFrame(() => {
          this.detectionTimeout = setTimeout(() => {
            runDetection();
          }, DETECTION_INTERVALL);
        });
      };
      runDetection();
    }, 1000);
  }

  stopDetection() {
    clearTimeout(this.detectionTimeout);
    this.sceneDetectionService?.stopDetection();
    this.endSimulation.emit();
  }

  startRecording(videoStream: MediaStream): void {
    window.MediaRecorder = class extends window.MediaRecorder {
      constructor(stream: MediaStream, options: MediaRecorderOptions) {
        super(stream, {
          ...options,
          videoBitsPerSecond: 1000000,
        });
      }
    };
    this.mediaRecorder = new window.MediaRecorder(videoStream);

    this.mediaRecorder.ondataavailable = (event: { data: Blob }) => {
      this.chunks.push(event.data);
    };

    this.mediaRecorder.onstop = () => {
      if (this.chunks.length > 0) {
        this.uploadBlob = new Blob(this.chunks, { type: 'video/mp4' });
        if (this.uploadBlob.size > 0) {
          this.sceneStore.setUploadVideo({
            id: uuidv4(),
            mimeType: 'video/mp4',
            blob: this.uploadBlob,
          });
        }
      }
    };

    this.mediaRecorder.start();
  }

  stopRecording(): void {
    this.stopDetection();
    if (this.mediaRecorder) {
      this.mediaRecorder.stop();
    }
  }

  recordingTimeString: Signal<string> = computed(() => {
    let hours = '' + Math.floor(this.recordingTime() / 3600);
    let minutes = '' + Math.floor((this.recordingTime() % 3600) / 60);
    let seconds = '' + Math.floor((this.recordingTime() % 3600) % 60);

    if (Number(hours) < 10) {
      hours = '0' + hours;
    } else {
      hours = '' + hours;
    }
    if (Number(minutes) < 10) {
      minutes = '0' + minutes;
    } else {
      minutes = '' + minutes;
    }
    if (Number(seconds) < 10) {
      seconds = '0' + seconds;
    } else {
      seconds = '' + seconds;
    }
    return minutes + ':' + seconds;
  });

  startRecordingTimer() {
    if (!this.isExerciseStarted()) {
      this.isExerciseStarted.set(true);
      this.delayedTimer = setTimeout(() => {
        clearInterval(this.timerInterval);
        this.recordingStartTime = new Date(
          new Date().toUTCString(),
        ).toISOString();
        this.timerInterval = setInterval(() => {
          this.recordingTime.set(this.recordingTime() + 1);
        }, 1000);
      }, 3000);
    }
  }

  stopRecordingTimer() {
    clearTimeout(this.delayedTimer);
    clearInterval(this.timerInterval);
    this.isExerciseStopped.set(true);
    this.recordingEndTime = new Date(new Date().toUTCString()).toISOString();
  }
}
